<!--
  FeatureBadge — shared shell for the "Early Payout" and "Cashout" badges.

  Both badges used to be copy-pasted inline SVG across ~13 render sites
  (event-row previews + bet-slip odd rows). This component is the single
  source of truth for that markup, fed by a per-platform admin override
  fetched fire-and-forget in src/main.js (GET /v3/badge-icons/:platformId,
  same non-blocking pattern as the uiTranslations fetch) and merged into
  Vuex via settings/SET_BADGE_ICONS_CONFIG.

  Contract (settings/getBadgeIconConfig(badgeKey)):
    { enabled: Boolean, size: Number (px, default 16), text: { [locale]: String },
      imageEnabled: Boolean, imageUrl: String|null }
  A badgeKey missing from the response (or nothing fetched yet) resolves to
  { enabled: false, size: 16, text: {}, imageEnabled: false, imageUrl: null }
  — i.e. exactly today's look.

  Precedence (highest first):
    1. imageEnabled === true AND imageUrl is a non-empty string -> render the
       admin-uploaded image (S3-hosted SVG/PNG) as a plain <img>. Rendered via
       <img>, NEVER inlined into the DOM — an <img> never executes script
       content embedded in an SVG file, so this is the XSS-safe way to render
       an admin-uploaded, potentially-untrusted SVG. Do not switch this to
       inlining the fetched markup or to an <object>/<iframe> embed.
    2. Else "no override for the current locale" wins the same way as
       translation overrides do: if `enabled` is false, or there's no
       non-empty entry for `$i18n.locale`, the DEFAULT glyph renders (the bolt
       for earlyPayout, the "$" stroke for cashout). Only a non-empty
       per-locale override swaps in the custom text.
    3. Else the default glyph renders.

  Sizing: every call site used to hard-force 16×16 via CSS regardless of the
  svg's own width/height attributes. That's now driven by the `--badge-size`
  custom property (see stylenew.css `.early-payout-badge` / `.agency-dollar-badge`),
  set here from the `size` config so it works identically whether rendering
  the default glyph or the custom-text variant.
-->
<template>
  <img
    v-if="imageActive"
    :class="badgeClass"
    :src="config.imageUrl"
    :width="config.size"
    :height="config.size"
    :style="{ '--badge-size': config.size + 'px' }"
    alt=""
  />
  <svg
    v-else
    :class="badgeClass"
    :width="config.size"
    :height="config.size"
    viewBox="0 0 16 16"
    :fill="badgeKey === 'earlyPayout' ? 'currentColor' : 'none'"
    :style="{ '--badge-size': config.size + 'px' }"
  >
    <rect x="1" y="1" width="14" height="14" rx="3" fill="currentColor"/>

    <text
      v-if="customText"
      x="8"
      y="8.5"
      text-anchor="middle"
      dominant-baseline="central"
      :font-size="textFontSize"
      font-weight="700"
      fill="#212225"
    >{{ customText }}</text>

    <path
      v-else-if="badgeKey === 'earlyPayout'"
      d="M9.5 2.5L5.5 8.5H8.5L7.5 13.5L11.5 7.5H8.5L9.5 2.5Z"
      fill="#212225"
    />

    <path
      v-else
      d="M8 3.2V12.8M10.2 5.2C10.2 4.4 9.4 3.8 8.2 3.8C7 3.8 6.2 4.4 6.2 5.3C6.2 6.1 6.8 6.5 8 6.8C9.4 7.1 10.4 7.6 10.4 8.9C10.4 10.1 9.4 10.8 8 10.8C6.6 10.8 5.6 10.1 5.6 9"
      stroke="#212225"
      stroke-width="1.3"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
</template>

<script>
export default {
    name: 'FeatureBadge',
    props: {
        badgeKey: {
            type: String,
            required: true,
            validator: value => ['earlyPayout', 'cashout'].includes(value)
        }
    },
    computed: {
        config() {
            return this.$store.getters['settings/getBadgeIconConfig'](this.badgeKey);
        },
        // Precedence step 1: an admin-uploaded image wins over custom text
        // and over the default glyph whenever it's enabled and present.
        imageActive() {
            return this.config.imageEnabled === true &&
                typeof this.config.imageUrl === 'string' &&
                this.config.imageUrl.trim().length > 0;
        },
        customText() {
            if (!this.config.enabled) return null;
            const value = this.config.text?.[this.$i18n.locale];
            return (typeof value === 'string' && value.trim().length > 0) ? value : null;
        },
        // Roughly 55-60% of the badge size at the default 16px box; longer
        // overrides shrink further so 3-4 char text still fits the 14x14
        // rounded-square shell without overflowing it.
        textFontSize() {
            const len = this.customText ? this.customText.length : 0;
            if (len <= 2) return 9;
            if (len === 3) return 7;
            return 5.5;
        },
        badgeClass() {
            return this.badgeKey === 'earlyPayout' ? 'early-payout-badge' : 'agency-dollar-badge';
        }
    }
};
</script>
